/**
 * Theme-aware logo component.
 *
 * Renders the brand logo resolved by the ThemeProvider. Falls back to
 * the IPA text mark when no custom logo URL is configured. The component
 * reads the logo URL from the branding context rather than from CSS
 * custom properties so that the fallback can be rendered as accessible text.
 *
 * @module shared/theme/ThemeAwareLogo
 */

import React from 'react';
import { useTheme } from './ThemeContext';

/** Props for the ThemeAwareLogo component. */
export interface ThemeAwareLogoProps {
  /** Height of the logo image in CSS units. Defaults to "40px". */
  height?: string;
  /** Additional CSS class names applied to the root element. */
  className?: string;
}

/**
 * Returns true when url is an absolute HTTPS URL safe to use as an image src.
 *
 * Rejects http:, javascript:, data:, and relative URLs. A compromised
 * White-Label Service response must not be able to force the browser to load
 * content from an attacker-controlled origin or cause mixed-content warnings
 * on HTTPS pages (HIPAA: avoid leaking patient browsing context to external
 * servers).
 */
export function isSafeImageUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Renders the white-labeled brand logo or the IPA text fallback.
 *
 * When a logo URL is available in the branding config the component
 * renders an `<img>` element; otherwise it renders a styled text mark.
 * The `alt` text is always set to the entity's display name for
 * screen-reader accessibility.
 */
export function ThemeAwareLogo({ height = '40px', className }: ThemeAwareLogoProps): React.JSX.Element {
  const { branding } = useTheme();

  if (isSafeImageUrl(branding.logoUrl)) {
    return (
      <img
        src={branding.logoUrl}
        alt={branding.displayName}
        height={height}
        className={className}
        data-testid="theme-logo-image"
        style={{ height, width: 'auto', objectFit: 'contain', display: 'block' }}
      />
    );
  }

  return (
    <span
      className={className}
      data-testid="theme-logo-text"
      style={{
        fontFamily: 'var(--ipa-font-family, system-ui)',
        fontWeight: 700,
        fontSize: '1.25rem',
        color: 'var(--ipa-primary, #1a73e8)',
        letterSpacing: '-0.025em',
      }}
    >
      {branding.displayName}
    </span>
  );
}
